import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  User as AppUser,
  TestResult as UserTestResult,
  UserProfile,
  EducationLevel,
  PreferredStudyTime,
  LearningStyle,
  ChallengingTopic,
  AccessStatusReason
} from '@/models/UserTypes';
import { TestType } from '@/models/SharedTypes';
import { quizApiService } from '@/services/QuizApiService';
import { UserService } from '@/services/user/UserService';
import type { FirebaseAuthState } from '@/hooks/useFirebaseAuthState';
import { useNotificationInbox } from '@/stores/useNotificationInbox';
import { logger } from '@/utils/logger';
import { setGaSubscriptionStatus } from '@/utils/Analytics';

/** Owned by AuthProvider; read it through `useAuth`/`useUserData`, not directly. */
export interface UserDataState {
  userData: AppUser | null;
  isLoadingUserData: boolean;
  refreshUserData: () => Promise<void>;
  hasProfile: boolean;
  isSubscribed: boolean;
  lastRefresh: number;
}

export const useUserDataState = ({ currentUser, isAuthenticated }: FirebaseAuthState): UserDataState => {
  const [dataState, setDataState] = useState(() => ({
    userData: null as AppUser | null,
    isLoadingUserData: true, // Start as loading by default
    lastRefresh: 0,
    settledUid: null as string | null
  }));

  // Retry tracking via REFS, not state: the scheduled retry calls back into
  // refreshUserData, whose deps intentionally exclude the count — a state value
  // would be frozen at 0 in that closure, so the maxRetries guard never trips
  // and the backoff never grows → an infinite 1s getUserProfile retry storm.
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxRetries = 3;

  // Memoized refresh function to prevent unnecessary re-renders
  const refreshUserData = useCallback(async () => {
    if (!currentUser || !isAuthenticated) {
      setDataState(prev => ({ 
        ...prev, 
        userData: null, 
        isLoadingUserData: false 
      }));
      retryCountRef.current = 0; // Reset retry count when not authenticated
      return;
    }

    setDataState(prev => ({ ...prev, isLoadingUserData: true }));

    try {
      logger.info('useUserDataState', 'Fetching user data via Cloud Function', {
        attempt: retryCountRef.current + 1,
        maxRetries
      });

      // Fetch profile + test results IN PARALLEL. Test results need only auth,
      // not the profile response, so awaiting them sequentially (profile → then
      // results) just delayed the dashboard's first paint. A results failure is
      // non-fatal (empty array) so it can't reject the profile load.
      const userService = UserService.getInstance();
      const [profileResponse, testResultsArr] = await Promise.all([
        userService.getFullUserProfile(),
        quizApiService.getUserTestResults(20, 0).then(
          (r) => r.testResults,
          () => {
            logger.error('useUserDataState', 'Failed to fetch test results');
            return [] as UserTestResult[];
          }
        ),
      ]);

      if (profileResponse) {
        // Map Cloud Function response to AppUser type
        const profile = profileResponse.profile;
        const mappedProfile: UserProfile | null = profile ? {
          name: profile.name,
          examDate: profile.examDate ?? '',
          // Server-reconciled soonest scheduled exam (epoch ms); preferred by
          // examSchedule.entryFor over the legacy client date resolution.
          nextScheduledExamMs: profile.nextScheduledExamMs ?? undefined,
          studyGoal: profile.studyGoal ?? '',
          onboardingCompleted: profile.onboardingCompleted,
          lastUpdated: profile.lastUpdated ?? '',
          targetExam: profile.targetExam as TestType | undefined,
          educationLevel: profile.educationLevel as EducationLevel | undefined,
          preferredStudyTime: profile.preferredStudyTime as PreferredStudyTime | undefined,
          challengingTopics: (profile.challengingTopics ?? []) as ChallengingTopic[],
          learningStyle: profile.learningStyle as LearningStyle | undefined,
          diagnostic: profile.diagnostic ?? null,
          schoolCode: profile.schoolCode ?? null,
        } : null;

        const userData: AppUser = {
          id: profileResponse.id,
          email: profileResponse.email,
          profile: mappedProfile,
          quizProgress: null,
          subscriptionStatus: (profileResponse.subscriptionStatus as AppUser['subscriptionStatus']) ?? 'inactive',
          isSubscribed: profileResponse.isSubscribed,
          // Authoritative server-computed access decision (single source of truth).
          hasFullAccess: profileResponse.hasFullAccess,
          // Permanent one-time ($129) access. Drives the /pricing redirect for
          // lifetime buyers without affecting trial users (who lack this flag).
          hasLifetimeAccess: profileResponse.hasLifetimeAccess,
          accessStatus:
            typeof profileResponse.accessReason === 'string'
              ? { reason: profileResponse.accessReason as AccessStatusReason }
              : undefined,
          // Trial fields kept for the trial-status UI (banner + countdown), NOT
          // for access: access is the server-computed hasFullAccess above and is
          // never re-derived from these (the hasFullAccess() fallback no longer
          // recomputes — it returns false).
          isTrialActive: profileResponse.isTrialActive,
          createdAt: profileResponse.createdAt ?? undefined,
          trialStartDate: profileResponse.trialStartDate ?? undefined,
          trialEndDate: profileResponse.trialEndDate ?? undefined,
          trialWeek: profileResponse.trialWeek ?? null,
          stripeCustomerId: profileResponse.stripeCustomerId ?? undefined,
          subscriptionId: profileResponse.subscriptionId ?? undefined,
          totalQuestionsAttempted: profileResponse.totalQuestionsAttempted,
          correctAnswers: profileResponse.correctAnswers,
          studyStreak: profileResponse.studyStreak,
          masteryLevel: profileResponse.masteryLevel,
          averageScore: profileResponse.averageScore,
          // Server-computed derived fields (single-scale): the client should read
          // these rather than recomputing accuracy/mastery from raw counts.
          accuracy: profileResponse.accuracy,
          masteryPercent: profileResponse.masteryPercent,
          completedTests: profileResponse.completedTests,
          totalStudyHours: profileResponse.totalStudyHours,
          // "Not ready yet" email pause, for the Settings card (null when none is in force).
          studyPausedUntil: profileResponse.studyPausedUntil ?? null,
          testResults: {},
          wrongAnswers: [],
        };

        // Apply the test results fetched in parallel above (auth-gated by profile).
        if (userData.profile) {
          userData.testResults = testResultsArr.reduce<Record<string, UserTestResult>>((acc, result) => {
            acc[result.id] = result;
            return acc;
          }, {});
        }

        // GA4 user property → lets reports/audiences segment by subscription state.
        setGaSubscriptionStatus(userData.subscriptionStatus);

        setDataState(prev => ({
          ...prev,
          userData,
          isLoadingUserData: false,
          lastRefresh: Date.now(),
          settledUid: currentUser.uid
        }));
        retryCountRef.current = 0; // Reset retry count on success
      } else {
        // User profile not found via Cloud Function - expected for new users
        if (retryCountRef.current < maxRetries) {
          const delay = Math.pow(2, retryCountRef.current) * 1000; // 1s, 2s, 4s
          logger.info('useUserDataState', 'User profile not found via Cloud Function, will retry', {
            userId: currentUser.uid,
            isAuthenticated,
            attempt: retryCountRef.current + 1,
            maxRetries
          });
          retryCountRef.current += 1;
          // Schedule a retry with exponential backoff. Reads retryCountRef (not a
          // stale state value) so the guard above actually trips after maxRetries.
          retryTimerRef.current = setTimeout(() => {
            refreshUserData();
          }, delay);
        } else {
          logger.warn('useUserDataState', 'User profile not found after max retries', {
            userId: currentUser.uid,
            isAuthenticated,
            maxRetries
          });
          retryCountRef.current = 0;
          setDataState(prev => ({
            ...prev,
            userData: null,
            isLoadingUserData: false,
            lastRefresh: Date.now(),
            settledUid: currentUser.uid
          }));
        }
      }
    } catch (error) {
      logger.error('useUserDataState', 'Error fetching user data', { error });
      setDataState(prev => ({
        ...prev,
        userData: null,
        isLoadingUserData: false,
        lastRefresh: Date.now(),
        settledUid: currentUser.uid
      }));
    }
  }, [currentUser, isAuthenticated]); // 🚨 CRITICAL FIX: Remove retryCount from dependencies to prevent function recreation

  // 🚨 CRITICAL FIX: Track subscription state to prevent re-subscriptions
  // Using ref to avoid putting Zustand functions in dependencies (causes read explosion)
  const subscribedUidRef = useRef<string | null>(null);

  // Auto-refresh user data when auth state changes
  // CRITICAL FIX: Use getState() to avoid unstable function references in dependencies
  useEffect(() => {
    const { subscribe, unsubscribe } = useNotificationInbox.getState();

    if (isAuthenticated && currentUser) {
      // Only subscribe if we haven't already for this user
      if (subscribedUidRef.current !== currentUser.uid) {
        subscribedUidRef.current = currentUser.uid;
        refreshUserData();
        // Subscribe to real-time notifications
        subscribe(currentUser.uid);
      }
    } else {
      // Only unsubscribe if we were subscribed
      if (subscribedUidRef.current !== null) {
        subscribedUidRef.current = null;
        unsubscribe();
      }
      setDataState(prev => ({
        ...prev,
        userData: null,
        isLoadingUserData: false,  // Not loading when not authenticated
        settledUid: null
      }));
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      retryCountRef.current = 0; // Reset retry count when not authenticated
    }

    // Cleanup on unmount
    return () => {
      const { unsubscribe: unsub } = useNotificationInbox.getState();
      unsub();
      subscribedUidRef.current = null;
      // Stop any pending retry so it can't fire after unmount / auth change.
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    };
  }, [isAuthenticated, currentUser?.uid]); // Only depend on auth state and uid, NOT Zustand functions

  // Derived, not set in the effect: the render where auth first reports a uid
  // runs before the fetch effect, and must already read as loading.
  const uid = isAuthenticated ? currentUser?.uid ?? null : null;
  const isLoadingUserData = dataState.isLoadingUserData || (uid !== null && dataState.settledUid !== uid);

  return React.useMemo(() => ({
    userData: dataState.userData,
    isLoadingUserData,
    refreshUserData,
    hasProfile: !!dataState.userData?.profile,
    isSubscribed: !!dataState.userData?.isSubscribed,
    lastRefresh: dataState.lastRefresh
  }), [
    dataState.userData, 
    isLoadingUserData, 
    dataState.lastRefresh,
    refreshUserData
  ]);
};
